import { Injectable, OnDestroy } from '@angular/core';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ConfigurationService } from './configuration.service';
import { Subscription } from 'rxjs';
import { Router, ResolveEnd, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Configuration } from './configuration.service.models';

@Injectable({
  providedIn: 'root'
})
export class AppInsightsService implements OnDestroy {

  private configuration: Configuration;
  private subscription: Subscription = new Subscription();
  private appInsights: ApplicationInsights;

  constructor(configurationService: ConfigurationService, router: Router) {
    this.configuration = configurationService.getConfiguration();
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: this.configuration.appInsightsKey
      }
    });
    this.appInsights.loadAppInsights();
    this.appInsights.trackPageView();

    this.subscription.add(router.events.pipe(filter(event => event instanceof ResolveEnd))
      .subscribe((event: ResolveEnd) => {
        const activatedComponent = this.getActivatedComponent(event.state.root);
        if (activatedComponent) {
          this.logNavigation(`${activatedComponent.name}`, event.urlAfterRedirects);
        }
      }));
  }

  ngOnDestroy(): void {
    void this.appInsights.flush();
    this.subscription.unsubscribe();
  }

  public addTelemetryInitializer(cloudRoleName: string, cloudRoleInstance: string): void {
    const telemetryInitializer = (envelope: ITelemetryItem) => {
      envelope.tags['ai.cloud.role'] = cloudRoleName;
      envelope.tags['ai.cloud.roleInstance'] = cloudRoleInstance;
    };
    this.appInsights.addTelemetryInitializer(telemetryInitializer);
  }

  public setUserId(userId: string): void {
    this.appInsights.setAuthenticatedUserContext(userId);
  }

  public clearUserId(): void {
    this.appInsights.clearAuthenticatedUserContext();
  }

  public logNavigation(
    name?: string,
    uri?: string,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number }
  ): void {
    this.appInsights.trackPageView({
      name,
      uri,
      properties,
      measurements
    });
    this.flushIfDevelopment();
  }

  public logEvent(name: string, properties?: { [key: string]: string }, measurements?: { [key: string]: number }): void {
    this.appInsights.trackEvent({
      name,
      properties,
      measurements
    });
    this.flushIfDevelopment();
  }

  public logError(
    exception: Error,
    properties?: { [name: string]: string },
    measurements?: { [name: string]: number }
  ): void {
    this.appInsights.trackException({
      exception,
      properties,
      measurements
    });
    this.flushIfDevelopment();
  }

  private flushIfDevelopment() {
    if (this.configuration.production) {
      return;
    }

    void this.appInsights.flush();
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): { name: string } {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component as { name: string };
  }
}
